import type { RequestOverrides } from '@wix/http-client';
import type { RichContent } from '@wix/ricos';
import { type IHttpClient } from '@wix/yoshi-flow-editor';
import {
  getPostPage,
  getPostPagePreview,
} from '@wix/ambassador-blog-frontend-adapter-public-v2-post-page/http';
import { type Post } from '@wix/ambassador-blog-v3-post/types';
import type { ErrorHandlerPublicAPI } from '@wix/yoshi-flow-editor/build/cjs/exports/error-handler';
import { createAction, fetchTagsSuccess, isCountableNode } from '@wix/communities-blog-client-common';
import { type PostPageThunkAction } from '@app/external/post-page/types';
import { type PlatformApi } from '../controller/platform-api';
import { getLanguageCode } from '../selectors/locale-selectors';
import { getDemoPosts } from '../services/demo-posts';
import getEnvironment from '../services/get-environment';
import { normalizePost, normalizePostV3 } from '../services/post-utils';
import { getCategoryIds } from '../store/categories/categories-selectors';
import type { GetState, NormalizedPost } from '../types';
import type { FlowAPI } from '../types/platform-types';

export const FETCH_POST_REQUEST = 'post/FETCH_REQUEST';
export const FETCH_POST_SUCCESS = 'post/FETCH_SUCCESS';
export const FETCH_POST_FAILURE = 'post/FETCH_FAILURE';

interface PreviewPostParams {
  includeDraft?: boolean;
  instance?: string;
  previewTextParagraph?: string;
}

interface MakeRequestParams {
  includeDraft?: boolean;
  instance?: string;
  previewTextParagraph?: string;
  httpClient: IHttpClient;
  errorHandler: ErrorHandlerPublicAPI;
  languageCode: string;
  dispatch: Function;
  getState: GetState;
  platformApi: PlatformApi;
  flowAPI: FlowAPI;
}

export const fetchPostRequest = createAction(
  FETCH_POST_REQUEST,
  (payload: { postSlug: string }) => payload,
);
export const fetchPostSuccess = createAction(
  FETCH_POST_SUCCESS,
  (payload: { post: NormalizedPost; postSlug: string }) => payload,
);
export const fetchPostFailure = createAction(
  FETCH_POST_FAILURE,
  (payload: { postSlug: string; error: unknown }) => payload,
);

export class FetchPostError extends Error {
  readonly name = 'FetchPostError';
  readonly status = 404;
}

const makeRequestToPlatformizedApi = async (
  postSlugOrId: string,
  {
    includeDraft,
    instance,
    previewTextParagraph,
    httpClient,
    errorHandler,
    languageCode,
    dispatch,
    platformApi,
    flowAPI,
  }: MakeRequestParams,
) => {
  const platformizedParams = {
    translationsName: 'main',
    languageCode,
  };
  const { withErrorHandler, getResolvedError } = errorHandler;

  const request = includeDraft
    ? getPostPagePreview({
        draftPostId: postSlugOrId,
        ...platformizedParams,
      })
    : getPostPage({
        postId: postSlugOrId,
        ...platformizedParams,
      });

  try {
    const requestOverrides: RequestOverrides | undefined = instance
      ? {
          signedInstance: instance,
        }
      : undefined;

    const postPageResponse = await withErrorHandler(
      () => httpClient.request(request, requestOverrides),
      {
        errorCodesMap: {
          applicationError: { NOT_FOUND: false, PERMISSION_DENIED: false },
        },
      },
    );

    const postPage = postPageResponse.data?.postPage;
    const post = postPage?.post;

    if (!post || !postPage || typeof post.slug === 'undefined') {
      throw new FetchPostError('Post not found');
    }

    if (previewTextParagraph) {
      try {
        const bannerRequest = await withErrorHandler(
          () =>
            httpClient.get<{ banner: RichContent }>(
              `/_api/paywall-server/rich-content-banners/default?lang=${
                platformApi.site.language ?? 'en'
              }&site=${platformApi.location.baseUrl ? platformApi.location.baseUrl : '[site-url]'}`,
              {
                headers: {
                  instance,
                },
              },
            ),
          { errorCodesMap: {} },
        );

        post.paywallRichContent = bannerRequest.data?.banner;
        post.preview = true;

        if (instance) {
          const paragraphCount = Number(previewTextParagraph) ?? 0;
          const { truncateRichContent } = await import('@wix/ricos');
          post.richContent = truncateRichContent(
            post.richContent as RichContent,
            { nodeCount: paragraphCount },
            { keepEmptyParagraphs: true, selectCountableNodes: isCountableNode },
          );
        }
      } catch (error) {
        getResolvedError(error);
        throw error;
      }
    }

    dispatch(fetchTagsSuccess(postPage.tags ?? []));

    return {
      ...normalizePostV3(post),
      tags: postPage.tags ?? [],
      /*
        To maintain compatibility with the old post page, we need to return the slug in the slugs array.
        It is used in the post page to determine if the post is avialable by slug or not.
      */
      slugs: [post.slug, postSlugOrId],
    };
  } catch (error: any) {
    if (!(error instanceof FetchPostError)) {
      getResolvedError(error);
    }

    if (error?.response?.status) {
      platformApi.seo.setSeoStatusCode(error.response.status);
    } else {
      platformApi.seo.setSeoStatusCode(500);
    }

    throw error;
  }
};

export const fetchPost = (
  postSlug: string,
  { includeDraft, instance, previewTextParagraph }: PreviewPostParams = {},
): PostPageThunkAction<NormalizedPost> => {
  return (dispatch, getState, extraArgs) => {
    const { httpClient, platformApi, flowAPI, errorHandler } = extraArgs;
    dispatch(fetchPostRequest({ postSlug }));
    const state = getState();

    const promise = makeRequestToPlatformizedApi(postSlug, {
      httpClient,
      errorHandler,
      languageCode: getLanguageCode(state),
      includeDraft,
      previewTextParagraph,
      instance,
      dispatch,
      getState,
      platformApi,
      flowAPI,
    });

    return completeFetchPost(postSlug, promise)(dispatch, getState, extraArgs);
  };
};

export const preFetchPost =
  (
    postSlug: string,
    { includeDraft, instance }: PreviewPostParams = {},
  ): PostPageThunkAction<NormalizedPost> =>
  (dispatch, getState, { httpClient, platformApi, flowAPI, errorHandler }) => {
    dispatch(fetchPostRequest({ postSlug }));

    const state = getState();
    const languageCode = getLanguageCode(state);

    return makeRequestToPlatformizedApi(postSlug, {
      languageCode,
      includeDraft,
      instance,
      httpClient,
      errorHandler,
      dispatch,
      getState,
      platformApi,
      flowAPI,
    });
  };

export const completeFetchPost =
  (
    postSlug: string,
    preFetchResult: ReturnType<typeof makeRequestToPlatformizedApi>,
  ): PostPageThunkAction<NormalizedPost> =>
  async (dispatch, getState, { platformApi, httpClient, aggregatorRequest, errorHandler }) => {
    try {
      let post: NormalizedPost | Post;
      let capturedError;

      try {
        post = await preFetchResult;
      } catch (e) {
        capturedError = e && typeof e === 'object' && 'response' in e ? e.response : e;

        if (
          capturedError &&
          typeof capturedError === 'object' &&
          'status' in capturedError &&
          capturedError.status === 404 &&
          getEnvironment(platformApi).isEditorSegment
        ) {
          const resp = await getDemoPosts({
            httpClient,
            getState,
            query: { slugs: [postSlug] },
            errorHandler,
          });
          post = resp?.posts?.[0];
          if (post) {
            capturedError = null;
          }
        }
      }

      if (capturedError) {
        throw capturedError;
      }

      const normalizedPost = normalizePost({
        // @ts-expect-error
        post,
        blogCategoryIds: getCategoryIds(getState()),
      });

      dispatch(fetchPostSuccess({ post: normalizedPost, postSlug }));

      return normalizedPost;
    } catch (error) {
      dispatch(fetchPostFailure({ postSlug, error }));
      throw error;
    }
  };
